import * as React from 'react';

import { Divider, Header, VStack } from '@rbilabs/universal-components';
import { capitalize } from 'lodash-es';
import { ScrollView, StyleSheet, View } from 'react-native';

import { WEB_MAX_WIDTH_STYLE } from 'components/card-modal';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { StepProgressBar } from 'components/step-progress-bar';
import { LoyaltyQuestStepStatus } from 'generated/graphql-gateway';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

import { useQuestContext } from '../loyalty-quest';
import { isQuestCompleted, isStepCompleted } from '../loyalty-quest/utils';

import { ChallengeDetailsPendingStepsMessage } from './challenge-details-pending-steps';
import { EndDateBadge } from './end-date-badge';
import { IncentiveMessage } from './incentive-message';
import { QuestIncentive } from './quest-incentive';
import { QuestSteps } from './quest-steps';
import { QuestTerms } from './quest-terms';
import { QuestStepsSectionWrapper } from './styled';
import { IQuestDetailContentViewProps } from './types';

export const QuestDrawerContent = ({
  cmsQuestDetail,
  cmsQuestDetailUI,
  onCloseDrawer,
}: IQuestDetailContentViewProps) => {
  const { howToRedeem } = cmsQuestDetail;
  const howToRedeemSanityContent = howToRedeem?.localeRaw;

  const { user } = useAuthContext();
  const username = capitalize(user?.details.name ?? '');

  const { quest } = useQuestContext();
  const challengeHasSomeProgress = !!quest?.steps?.some(step => isStepCompleted(step.status));

  const isDesktop = useIsDesktopBreakpoint();

  const howItWorksMessage = t('howItWorks');
  const greatWorkMessage = t('questUserGreatWork', { username });
  const onYourWayMessage = t('questOnYourWay');

  const header = challengeHasSomeProgress ? (
    <View style={{ flexDirection: 'column' }}>
      {!!username && (
        <Header margin={0} variant="headerThree">
          {greatWorkMessage}
        </Header>
      )}
      <Header margin={0} variant="headerThree">
        {onYourWayMessage}
      </Header>
    </View>
  ) : (
    <Header margin={0} variant="headerThree">
      {howItWorksMessage}
    </Header>
  );

  const totalSteps = quest?.steps?.length || 0;
  const completedSteps =
    quest?.steps?.filter(step => step.status === LoyaltyQuestStepStatus.COMPLETED).length || 0;

  return (
    <View style={styles.pageContainer}>
      <View style={[styles.drawerContainer, isDesktop ? { alignItems: 'center' } : {}]}>
        <View style={isDesktop ? WEB_MAX_WIDTH_STYLE : {}}>
          <QuestIncentive
            cmsIncentive={cmsQuestDetail.incentive}
            cmsQuestDetail={cmsQuestDetail}
            onCloseDrawer={onCloseDrawer}
          />
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.scrollView}>
        <VStack padding={spacing.$1} style={isDesktop ? WEB_MAX_WIDTH_STYLE : {}}>
          <QuestStepsSectionWrapper>
            <View style={styles.headerContainer}>
              {header}
              {!isQuestCompleted(quest?.status) && <EndDateBadge quest={quest} />}
            </View>

            <SanityBlockRenderer content={howToRedeemSanityContent} />

            <View style={styles.progressBar}>
              <StepProgressBar totalSteps={totalSteps} completedSteps={completedSteps} />
              <ChallengeDetailsPendingStepsMessage
                totalSteps={totalSteps}
                completedSteps={completedSteps}
                quest={quest}
              />
            </View>

            <QuestSteps
              cmsQuestRequirementById={cmsQuestDetail.requirementByCmsId}
              cmsQuestDetail={cmsQuestDetail}
              cmsQuestDetailUI={cmsQuestDetailUI}
              onCloseDrawer={onCloseDrawer}
              quest={quest}
            />
            <IncentiveMessage incentiveType={quest.incentiveType} />
          </QuestStepsSectionWrapper>
          <Divider />
          <QuestTerms cmsQuestTerms={cmsQuestDetail.moreInfo} />
        </VStack>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  pageContainer: { flex: 1 },
  drawerContainer: { width: '100%', backgroundColor: primitive.$bbqBrown },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  scrollView: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: primitive.$mayoEggWhite,
  },
  progressBar: { padding: 5 },
});
